import React, { useRef, useEffect, useState, useCallback } from "react";
import { useAnimation } from "./AnimationContext";
import { Play, Pause, Maximize2, Minimize2, Clock } from "lucide-react";

export default function TimelineSequencer() {
  const {
    elements,
    isPlaying,
    currentTime,
    totalDuration,
    selectedActionId,
    setSelectedActionId,
    setSelectedElementId,
    getTimelineActions,
    updateAction,
    runGSAPPreview,
    pauseGSAPPreview,
    seekGSAPPreview,
  } = useAnimation();

  const [activeTab, setActiveTab] = useState("timeline");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const dragRef = useRef(null);
  const [dragState, setDragState] = useState(null);

  const timelineActions = getTimelineActions();

  const formatTimecode = (sec) => {
    const mins = Math.floor(sec / 60);
    const secs = Math.floor(sec % 60);
    const ms = Math.floor((sec % 1) * 100);
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}.${ms
      .toString()
      .padStart(2, "0")}`;
  };

  const handleMouseDown = (e, actionId, type) => {
    e.stopPropagation();
    const action = timelineActions.find((a) => a.id === actionId);
    if (!action) return;
    const state = {
      actionId,
      type,
      startX: e.clientX,
      startDelay: action.delay,
      startDuration: action.duration,
    };
    dragRef.current = state;
    setDragState(state);
  };

  const handleMouseMove = useCallback(
    (e) => {
      const drag = dragRef.current;
      if (!drag) return;
      const deltaSec = (e.clientX - drag.startX) / 150;

      if (drag.type === "move") {
        updateAction(drag.actionId, {
          delay: Math.max(0, parseFloat((drag.startDelay + deltaSec).toFixed(2))),
        });
      } else if (drag.type === "resize-right") {
        updateAction(drag.actionId, {
          duration: Math.max(0.1, parseFloat((drag.startDuration + deltaSec).toFixed(2))),
        });
      } else if (drag.type === "resize-left") {
        const newDelay = Math.max(0, parseFloat((drag.startDelay + deltaSec).toFixed(2)));
        const actualDelta = newDelay - drag.startDelay;
        updateAction(drag.actionId, {
          delay: newDelay,
          duration: Math.max(0.1, parseFloat((drag.startDuration - actualDelta).toFixed(2))),
        });
      }
    },
    [updateAction]
  );

  const handleMouseUp = useCallback(() => {
    dragRef.current = null;
    setDragState(null);
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const renderRuler = () => {
    const marks = [];
    const totalMs = totalDuration * 1000;
    for (let ms = 0; ms <= totalMs; ms += 500) {
      const left = (ms / 1000) * 150;
      marks.push(
        <div
          key={ms}
          className="absolute border-l border-gray-300 h-2 flex flex-col justify-end"
          style={{ left: `${left}px`, bottom: 0 }}
        >
          <span className="text-[9px] text-gray-400 font-semibold -ml-3 mb-2.5">{ms}ms</span>
        </div>
      );
    }
    return marks;
  };
  
  return (
    <div
      className={`bg-white border-t border-gray-200 flex flex-col select-none transition-all ${
        isFullscreen ? "fixed inset-0 z-50 h-full" : "h-[260px]"
      }`}
    >
      {/* Controls bar */}
      <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <div className="flex gap-2 text-xs font-semibold">
          <button
            onClick={() => setActiveTab("timeline")}
            className={`px-3 py-1.5 rounded transition ${
              activeTab === "timeline"
                ? "bg-white text-purpleColor shadow-sm"
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            Timeline View
          </button>
          <button
            onClick={() => setActiveTab("list")}
            className={`px-3 py-1.5 rounded transition ${
              activeTab === "list"
                ? "bg-white text-purpleColor shadow-sm"
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            List View
          </button>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={isPlaying ? pauseGSAPPreview : runGSAPPreview}
            disabled={timelineActions.length === 0}
            className="w-8 h-8 rounded-full bg-purpleColor hover:bg-[#7a1b51] text-white flex items-center justify-center transition focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isPlaying ? (
              <Pause className="w-4 h-4 fill-current" />
            ) : (
              <Play className="w-4 h-4 fill-current ml-0.5" />
            )}
          </button>

          <div className="text-xs font-mono font-bold text-gray-700">
            {formatTimecode(currentTime)} / {formatTimecode(totalDuration)}
          </div>

          <input
            type="range"
            min="0"
            max={totalDuration}
            step="0.05"
            value={currentTime}
            onChange={(e) => seekGSAPPreview(parseFloat(e.target.value))}
            className="w-48 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            style={{ accentColor: "#952264" }}
          />
        </div>

        <button
          onClick={() => setIsFullscreen(!isFullscreen)}
          className="text-gray-500 hover:text-gray-800 p-1.5"
        >
          {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
      </div>

      {/* Track area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Element Labels */}
        <div className="w-60 border-r border-gray-200 flex flex-col bg-gray-50">
          <div className="h-8 border-b border-gray-200" />
          <div className="flex-1 overflow-y-auto">
            {elements.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center text-gray-400 px-3">
                <Clock className="w-5 h-5 mb-1 opacity-30" />
                <p className="text-[10px]">No elements on canvas</p>
              </div>
            ) : (
              elements.map((el) => {
                const count = timelineActions.filter((a) => a.element_id === el.id).length;
                return (
                  <div
                    key={el.id}
                    className="h-12 border-b border-gray-100 flex items-center px-4 justify-between bg-white"
                  >
                    <span className="text-xs font-semibold text-gray-700 truncate max-w-[140px]">
                      {el.name}
                    </span>
                    {count > 0 && (
                      <span className="text-[10px] font-bold text-purpleColor bg-purpleColor/10 px-1.5 py-0.5 rounded-full">
                        {count} act
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Sequencer Tracks */}
        <div className="flex-1 flex flex-col overflow-hidden relative">
          {/* Ruler */}
          <div className="h-8 bg-gray-50 border-b border-gray-200 relative overflow-hidden flex items-end">
            <div
              className="absolute inset-0 flex relative"
              style={{ width: `${totalDuration * 150}px` }}
            >
              {renderRuler()}
            </div>
          </div>

          {/* Blocks */}
          <div className="flex-1 overflow-x-auto overflow-y-auto">
            <div
              className="relative min-h-full bg-slate-50/30"
              style={{ width: `${Math.max(800, totalDuration * 150 + 100)}px` }}
            >
              {/* Playhead */}
              <div
                className="absolute top-0 bottom-0 border-l-2 border-purpleColor z-10 pointer-events-none"
                style={{ left: `${currentTime * 150}px` }}
              >
                <div className="w-3 h-3 -ml-1.5 -mt-1 bg-purpleColor rounded-full" />
              </div>

              {elements.map((el) => {
                const acts = timelineActions.filter((a) => a.element_id === el.id);
                return (
                  <div
                    key={el.id}
                    className="h-12 border-b border-gray-100 flex items-center relative px-1 bg-white hover:bg-gray-50/50"
                  >
                    {acts.map((act) => (
                      <div
                        key={act.id}
                        className={`absolute h-8 rounded-md text-white flex items-center px-2 text-[10px] font-medium shadow-sm transition border group select-none cursor-grab ${
                          selectedActionId === act.id
                            ? "bg-lightPurple border-white/40 ring-2 ring-purpleColor"
                            : "bg-purpleColor hover:bg-lightPurple border-purpleColor/20"
                        }`}
                        style={{
                          left: `${act.delay * 150}px`,
                          width: `${act.duration * 150}px`,
                        }}
                        onClick={(e) => {
                          if (dragRef.current) return;
                          e.stopPropagation();
                          setSelectedElementId(act.element_id);
                          setSelectedActionId(selectedActionId === act.id ? null : act.id);
                        }}
                        onMouseDown={(e) => handleMouseDown(e, act.id, "move")}
                      >
                        <div
                          onMouseDown={(e) => handleMouseDown(e, act.id, "resize-left")}
                          className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize bg-lightPurple rounded-l opacity-0 group-hover:opacity-100 transition"
                        />
                        <span className="truncate w-full pr-1 select-none">
                          {act.type.toUpperCase()}: {act.ease.split(".")[0]}
                        </span>
                        <div
                          onMouseDown={(e) => handleMouseDown(e, act.id, "resize-right")}
                          className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize bg-lightPurple rounded-r opacity-0 group-hover:opacity-100 transition"
                        />
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
